import {
  booleanAttribute,
  Component,
  computed,
  contentChild,
  input,
  output,
} from '@angular/core';
import { BadgeColor, BadgeSize, BadgeTone } from './badge.types';
import { buildBadgeClasses } from './badge.utils';
import {
  NttBadgeStart,
  NttBadgeEnd,
  NttBadgeContent,
} from './directives/badge.directives';

@Component({
  selector: 'ntt-badge, ntt-badge-group',
  templateUrl: './badge.html',
  styleUrl: './badge.scss',
})
export class NttBadge {
  readonly color = input<BadgeColor>('gray');
  readonly size = input<BadgeSize>('md');
  readonly tone = input<BadgeTone>('color');
  readonly radius = input<'full' | 'small' | 'circle'>('full');
  readonly dot = input(false, { transform: booleanAttribute });
  readonly dotOutline = input(false, { transform: booleanAttribute });
  readonly dotColor = input<BadgeColor | undefined>(undefined);
  readonly avatarText = input<string | null>(null);
  readonly icon = input<string | null>(null);
  readonly iconPosition = input<'start' | 'end'>('start');
  readonly iconOnly = input(false, { transform: booleanAttribute });
  readonly iconOnlyShape = input<'default' | 'circle' | null>(null);
  readonly flat = input(false, { transform: booleanAttribute });
  readonly closable = input(false, { transform: booleanAttribute });

  readonly closed = output<void>();

  private readonly _startSlot = contentChild(NttBadgeStart);
  private readonly _endSlot = contentChild(NttBadgeEnd);
  private readonly _contentSlot = contentChild(NttBadgeContent);

  protected readonly hasStartSlot = computed(() => !!this._startSlot());
  protected readonly hasEndSlot = computed(() => !!this._endSlot());
  protected readonly hasContentSlot = computed(() => !!this._contentSlot());

  protected readonly classes = computed(() => {
    const shape =
      this.radius() === 'circle'
        ? 'circle'
        : this.radius() === 'full'
          ? 'pill'
          : 'default';
    const tone = this.tone();
    const isIconOnly = this.iconOnly();
    const iconOnlyShape =
      this.iconOnlyShape() ?? (tone === 'neutral' ? 'circle' : 'default');
    const shadow = !this.flat();
    const hasIconStart = this.icon() && this.iconPosition() === 'start';
    const hasIconEnd = this.icon() && this.iconPosition() === 'end';
    const isClosable = this.closable();
    const hasStartSlot = this.hasStartSlot();
    const hasEndSlot = this.hasEndSlot();
    const hasElementAtStart = isClosable || hasIconStart || hasStartSlot;
    const hasElementAtEnd = hasIconEnd || hasEndSlot;

    return buildBadgeClasses({
      size: this.size(),
      tone,
      color: this.color(),
      dotColor: this.dotColor(),
      shape,
      shadow,
      iconOnly: isIconOnly,
      iconOnlyShape: isIconOnly ? iconOnlyShape : undefined,
      extraClasses: [
        hasElementAtStart ? 'ntt-badge--no-padding-start' : null,
        hasElementAtEnd ? 'ntt-badge--no-padding-end' : null,
      ].filter(Boolean) as string[],
    });
  });

  protected readonly closeClasses = computed(() => {
    const shape =
      this.radius() === 'circle'
        ? 'rounded'
        : this.radius() === 'full'
          ? 'rounded'
          : 'square';
    const color = this.color();
    return `ntt-badge__close ntt-badge__close--${shape} ntt-badge__close--color-${color}`;
  });

  protected _onClose(event: MouseEvent): void {
    event.stopPropagation();
    this.closed.emit();
  }
}
